/* eslint-disable react-portal/no-react-router-use-navigate */
import { useCallback } from 'react';
import { t, Trans } from '@lingui/macro';
import { Box, Typography } from '@material-ui/core';
import { NavigateBefore } from '@material-ui/icons';
import { pick } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { useGlobalDataService } from '../../services/ApiDataService';
import { useRequestState } from '../../utilities/Hooks';
import { rethrowError } from '../../utilities/RequestUtilities';
import { getUrlSearchParam } from '../../utilities/UrlUtilities';
import NgpTextField from '../form/NgpTextField';
import { RowFlexLayout } from '../layout';
import Link from '../links/Link';
import { USE_FORM_DEFAULT_OPTIONS } from '../../utilities/FormUtilities';
import AuthButton from './AuthButton';
import AuthProgressButton from './AuthProgressButton';

export default function PasswordResetRequest() {
    const userNameOrEmail = getUrlSearchParam('userNameOrEmail', '');
    const formMethods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: { userNameOrEmail },
    });

    const { requestReset, isLoading, error } = useRequestResetHandler();

    return (
        <>
            <FormProvider {...formMethods}>
                <form onSubmit={formMethods.handleSubmit(requestReset)}>
                    <Typography variant="h6">
                        <Trans>Request Password Reset</Trans>
                    </Typography>

                    <NgpTextField
                        fieldName="userNameOrEmail"
                        label={<Trans>Email Address or Username</Trans>}
                        required
                        disabled={isLoading}
                        inputProps={{
                            size: 100,
                        }}
                    />

                    {error && (
                        <Typography variant="subtitle2" color="error">
                            {String(error)}
                        </Typography>
                    )}

                    <Box mt={2}>
                        <Typography variant="caption">
                            <Trans>
                                {t`We'll email you a reset code to your registered email address. If for
                            some reason you don't receive the email, be sure to check your 'Junk'
                            email folder.`}
                            </Trans>
                        </Typography>
                    </Box>

                    <AuthProgressButton type="submit" loading={isLoading} container={<Box mt={2} />}>
                        <Trans>Submit Password Request</Trans>
                    </AuthProgressButton>
                </form>
            </FormProvider>

            <RowFlexLayout mt={6} vCenter>
                <AuthButton component={Link} to="/login" startIcon={<NavigateBefore />} variant="text">
                    <Trans>Back to Login</Trans>
                </AuthButton>
                <Box flexGrow={1} />
                <AuthButton component={Link} to="/reset">
                    <Trans>I have a reset code</Trans>
                </AuthButton>
            </RowFlexLayout>
        </>
    );
}

function useRequestResetHandler() {
    const dataService = useGlobalDataService();
    const navigate = useNavigate();
    const {
        isLoading,
        error,
        methods: { onInit, onError },
    } = useRequestState();

    const requestReset = useCallback(
        async (data) => {
            try {
                onInit();
                const postData = pick(data, ['userNameOrEmail']);
                await dataService.post('/ipm/public/reset/email', postData, null, null, rethrowError);

                navigate('/reset');
            } catch (e) {
                onError(e);
            }
        },
        [dataService, navigate, onError, onInit]
    );

    return { requestReset, isLoading, error };
}
